/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import useResponsive from '../../hooks/useResponsive';
import onboardingData from '../../data/mockOnboard.json';
import JobCard from '../../components/JobCard';
import { useNavigate } from 'react-router-dom';
import { useCamera } from '../../hooks/useCamera';
import useRecording from '../../hooks/useRecording';
import { deleteVideo, getAllVideos, getVideo, initDB, saveVideo } from '../../utils/videoStorage';
import { getWithExpiry } from '../../utils/sessionStorage';
import Spinner from '../../components/Spinner';
import ToastMessage, { ToastMessageProps } from '../../components/ToastMessage';
import { useInternetConnection } from '../../utils/connection';
import { S3UploadMetadata } from '../../types/file';

/*
  Video Interview
 */
const Interview = () => {
  const isMobile = useResponsive('md');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [hasRecorded, setHasRecorded] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [remainingTries, setRemainingTries] = useState(3);
  const [job, setJob] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const applicantGuid = getWithExpiry('applicantGuid') || undefined;
  const jobId = getWithExpiry('jobId') || undefined;
  const interviewId = getWithExpiry('interviewId') || undefined;
  const companyId = getWithExpiry('companyId') || undefined;
  const ttsAudio = getWithExpiry('ttsAudio') || undefined;
  const isTTSPlayedRef = useRef(false);
  const navigate = useNavigate();
  const [startTime, setStartTime] = useState<number | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasStoppedRef = useRef(false); // Prevent multiple stops
  const questionAudioRef = useRef<HTMLAudioElement | null>(null);
  const questionnaires = getWithExpiry('questionnaires');
  const currentQuestion = questionnaires[currentQuestionIndex];
  const totalQuestions = questionnaires.length;
  const selectedCameraDeviceId = getWithExpiry('selectedCameraDeviceId') || null;
  const selectedAudioDeviceId = getWithExpiry('selectedAudioDeviceId') || null;

  const s3MetaData: S3UploadMetadata = {
    interviewId: interviewId || '',
    dateInterviewed: new Date().toISOString(),
    companyId: companyId || '',
  };

  const playbackVideoRef = useRef<HTMLVideoElement>(null);
  
  // asnyc loading state for buttons
  const [processingButtons, setProcessingButtons] = useState<{[key: string]: boolean}>({
    start: false,
    stop: false,
    reRecord: false,
    submit: false,
  });

  // toast message
  const [showToast, setShowToast] = useState(selectedCameraDeviceId === null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastMessageProps>({
    hasTitle: true,
    title: '',
    message: '',
    type: 'info',
    isMobile: isMobile,
  });

  const isConnected = useInternetConnection();

  useEffect(() => {
    if (!isConnected) {
      const connectionMessage = `You are offline. Please reconnect to continue.(${Date.now()})`;
      setToast({
        hasTitle: true,
        title: 'Connection Lost',
        message: connectionMessage.replace(/\(\d+\)$/, ''),
        type: 'error',
        isMobile: isMobile,
        isOpen: true,
      });
      setShowToast(true);
    } else {
      setShowToast(false);
      setError(null);
    }
  }, [isConnected]);

  // general error handling
  useEffect(() => {
    if(error){
      setToast({
        hasTitle: true,
        title: 'Error',
        message: error,
        type: 'error',
        isMobile: isMobile,
        isOpen: showToast,
      });
      setShowToast(true);
    }
  }, [error]);

  const handleButtonClick = async (buttonId: string, action: () => Promise<void>) => {
    const startTime = new Date();
    try {
      console.log(buttonId, ' Starting button action', new Date().toISOString());
      setProcessingButtons(prev => ({ ...prev, [buttonId]: true }));
      console.log(buttonId, ' Button action started', new Date().toISOString());
      await action();
      console.log(buttonId, ' Button action completed', new Date().toISOString());
    } finally {
      console.log(buttonId, ' Button action finally', new Date().toISOString());
      console.log("Time taken:", new Date().getTime() - startTime.getTime(), "ms");
      setProcessingButtons(prev => ({ ...prev, [buttonId]: false }));
    }
  };


  // camera hook
  const { isCameraOn, videoRef, toggleCamera, setIsCameraOn, deviceRemoved, setDeviceRemoved } = useCamera();
  const {
    isRecording,
    recordedUrl,
    startRecording,
    stopRecording,
    clearRecording,
    setIsRecording,
    startStreamedRecording,
    stopStreamedRecording,
    handleSubmitStreamRecording,
    error: recordingError,
    audioRecordingError,
    deviceRemovedDuringRecording,
    setDeviceRemovedDuringRecording,
    setError: setRecordingError
  } = useRecording();

   // Handle camera device removal errors
   useEffect(() => {
    if (deviceRemoved) {
      console.log('Device removed during recording');
      let message = `Your device has been disconnected. Please reconnect your device and try again. (${Date.now()})`;
      setToast({
        hasTitle: true,
        title: 'Device Disconnected',
        message: message.replace(/\(\d+\)$/, ''),
        type: 'error',
        isMobile: isMobile,
        isOpen: true,
      });
      setShowToast(true);
      
      // If recording was in progress, stop it
      if (isRecording) {
        setStopRecording();
      }
    }
    else{
      setRecordingError(null);
      setDeviceRemoved(false);
      setShowToast(false);
    }
  }, [deviceRemoved]);

  // Handle audio device removal during recording
  useEffect(() => {
    if (deviceRemovedDuringRecording) {
      console.error('Device removed during recording');
      let message = `Your device has been disconnected during recording. Please reconnect your device and try again. (${Date.now()})`;
      setToast({
        hasTitle: true,
        title: 'Device Disconnected',
        message: message.replace(/\(\d+\)$/, ''),
        type: 'error',
        isMobile: isMobile,
        isOpen: true,
      });
      setShowToast(true);
      
      // If recording was in progress, stop it
      if (isRecording) {
        setStopRecording();
      }
    }
    else{
      setRecordingError(null);
      setDeviceRemovedDuringRecording(false);
      setShowToast(false);
    }
  }, [deviceRemovedDuringRecording]);
  
  // audio recording error handling
  useEffect(() => {
    if(audioRecordingError){
      setToast({
        hasTitle: true,
        title: 'Are you talking?',
        message: audioRecordingError.replace(/\(\d+\)$/, ''),
        type: 'error',
        isMobile: isMobile,
        isOpen: showToast,
      });
      setShowToast(true);
    }else{
      setShowToast(false);
    }
  }, [audioRecordingError]);

  const isStartButtonLoading = processingButtons.start || isRecording;

  // Enhanced device error handling
  useEffect(() => {
    if (selectedCameraDeviceId === null || recordingError || deviceRemoved || deviceRemovedDuringRecording) {
      console.log('Device error detected:', { selectedCameraDeviceId, recordingError, deviceRemoved, deviceRemovedDuringRecording });
      
      let errorMessage = 'Please check your browser permissions and try again.';
      
      if (deviceRemoved) {
        errorMessage = `Your device has been disconnected. Please reconnect your device and try again. (${Date.now()})`;
      } else if (deviceRemovedDuringRecording) {
        errorMessage = `Your device has been disconnected during recording. Please reconnect your device and try again. (${Date.now()})`;
      } else if (recordingError) {
        errorMessage = recordingError;
      }
      
      setToast({
        hasTitle: true,
        title: 'Device Error',
        message: errorMessage.replace(/\(\d+\)$/, ''),
        type: 'error',
        isMobile: isMobile,
        isOpen: showToast,
      });
      setShowToast(true);

      // Clear errors after a delay to ensure toast is shown
      const timeoutId = setTimeout(() => {
        setError(null);
        setRecordingError(null);
        setDeviceRemoved(false);
        setDeviceRemovedDuringRecording(false);
      }, 5000);

      return () => clearTimeout(timeoutId);

    }
  }, [selectedCameraDeviceId, recordingError, deviceRemoved, deviceRemovedDuringRecording]);


  // on mount, check if applicationId and jobId are set
  useEffect(() => {
    setJob(getWithExpiry('job'));
  }, [jobId, navigate]);

  useEffect(() => {
    const initializeCamera = async () => {
      try {
        // console.log('Initializing camera');
        await toggleCamera(selectedCameraDeviceId || undefined);
        // console.log('Camera initialized');
      } catch (error) {
        console.error('Failed to initialize camera:', error);
      }
    }

    initializeCamera();
  }, [currentQuestionIndex]);

  useEffect(() => {
    isTTSPlayedRef.current = false;
  }, [currentQuestionIndex]);

  // Add this useEffect to play TTS audio when question changes
  useEffect(() => {
    if(isTTSPlayedRef.current) return;
    // Clean up previous audio if exists
    if (questionAudioRef.current) {
      questionAudioRef.current.pause();
      questionAudioRef.current.src = '';
    }
    
    if (ttsAudio && Array.isArray(ttsAudio) && ttsAudio.length > currentQuestionIndex) {
      // Create a URL that the browser can access by converting file paths to API endpoints
      const audioPath = ttsAudio[currentQuestionIndex];
      // Extract just the filename from the path
      const filename = audioPath.split('/').pop() || audioPath.split('\\').pop();
      
      // Create a proper audio URL - this must match your server route exactly
      const audioUrl = `/api/v1/media/audio/stream/${encodeURIComponent(filename)}`;
      
      console.log(`Playing TTS audio for question ${currentQuestionIndex} from: ${audioUrl}`);
      
      // Create a new audio element with the HTTP URL
      const audioElement = new Audio(audioUrl);
      questionAudioRef.current = audioElement;
      
      // Play the audio automatically
      audioElement.play().catch(error => {
        console.error('Failed to play TTS audio:', error);
      });
      
      // Add event listener for debugging
      audioElement.addEventListener('ended', () => {
        console.log('TTS audio playback finished');
      });
    }
    else {
      console.log("No TTS audio found or invalid index");
    }
    
    // Cleanup function
    return () => {
      if (questionAudioRef.current) {
        questionAudioRef.current.pause();
        questionAudioRef.current.src = '';
      }
    };
  }, [currentQuestionIndex]);


  // Handle timer
  useEffect(() => {
    if (isRecording && !startTime) {
      const now = Date.now();
      setStartTime(now);
      hasStoppedRef.current = false;
      
      timerIntervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - now) / 1000);
        setRecordingTime(elapsed);
        
        // Hard stop at 120 seconds
        if (elapsed >= 120 && !hasStoppedRef.current) {
          hasStoppedRef.current = true;
          console.log('Timer auto-stop triggered at:', elapsed, 'seconds');
          
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
          }
          
          // Force stop recording immediately
          setStopRecording();
        }
      }, 1000);
    } else if (!isRecording) {
      // Reset when not recording
      setStartTime(null);
      hasStoppedRef.current = false;
      
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }
    
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [isRecording]);

  // Add at the beginning of your component:
  useEffect(() => {
    // Clear all recordings on initial load
    const clearAllRecordings = async () => {
      try {
        const db = await initDB();
        const transaction = db.transaction(['videos'], 'readwrite');
        const store = transaction.objectStore('videos');
        store.clear();
        // console.log('All recordings cleared');
      } catch (error) {
        console.error('Failed to clear recordings:', error);
      }
    };

    clearAllRecordings();
  }, []);

  const handleRecordButtonClick = async () => {
    // await new Promise(resolve => setTimeout(resolve, 2000));
    if(!isConnected){
      const connectionMessage = `You are offline. Please reconnect to continue.(${Date.now()})`;
      setToast({
        hasTitle: true,
        title: 'Connection Lost',
        message: connectionMessage.replace(/\(\d+\)$/, ''),
        type: 'error',
        isMobile: isMobile,
        isOpen: true,
      });
      setShowToast(true);
      return;
    }

    if(selectedCameraDeviceId === null || recordingError || deviceRemoved || deviceRemovedDuringRecording){
      console.log("Device error");
      let errorMessage = `Please check your browser permissions and try again. (${Date.now()})`;
      
      if (deviceRemoved) {
        errorMessage = `Your camera device has been disconnected. Please reconnect your camera or select a different device. (${Date.now()})`;
        console.error(errorMessage);
      } else if (recordingError) {
        errorMessage = recordingError;
      }
      
      setError(`${errorMessage} (${Date.now()})`);
      return;
    }
    
    if(remainingTries === 0){
      setError(`No more recording attempts remaining. (${Date.now()})`);
      return;
    }
    // setIsRecording(true);


    // mute playback video
    if(questionAudioRef.current){
      questionAudioRef.current.pause();
      questionAudioRef.current.src = '';
    }
    if (!isCameraOn) {
      // Turn on camera first
      await toggleCamera();
      setIsCameraOn(true);
    }

    if (isRecording) {
      console.log('handleRecordButtonClick: Stopping streamed recording');
      await stopStreamedRecording();
    } else {
      await startStreamedRecording(applicantGuid, currentQuestionIndex, currentQuestion.questionText, currentQuestionIndex === totalQuestions - 1, totalQuestions, s3MetaData);
    }
  };  

  const goToNextQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      // Don't clear recording here - we want to keep it stored
      setHasRecorded(false);
      setRemainingTries(3);
    }
  };

  const formatTimeRemaining = () => {
    const secondsRemaining = 120 - recordingTime;
    const minutes = Math.floor(secondsRemaining / 60);
    const seconds = secondsRemaining % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  // Play recorded video
  const playRecording = () => {
    try{
      setError(null);
      if (playbackVideoRef.current && recordedUrl) {
        playbackVideoRef.current.src = recordedUrl;
        // playbackVideoRef.current.style.display = 'block';
        playbackVideoRef.current.muted = false;
        playbackVideoRef.current.volume = 1.0;
  
        // Play the video with audio
        playbackVideoRef.current.play().catch(e => console.error('Playback error:', e));
      }
    }catch(error){
      console.error('Error playing recording:', error);
      setError(`Unable to play recording. (${Date.now()})`);
    }
    
  };

  // Replay just the audio
  const playAudio = () => {
    try{
      setError(null);
      setIsMuted(!isMuted);
      if (playbackVideoRef.current && recordedUrl) {
        playbackVideoRef.current.muted = isMuted;
        playbackVideoRef.current.play().catch(e => console.error('Audio playback error:', e));

        // cleanup
        const onEnded = () => {
          // console.log('Audio playback finished');
          playbackVideoRef.current?.removeEventListener('ended', onEnded);
        };

        playbackVideoRef.current.addEventListener('ended', onEnded);
      }
    }catch(error){
      console.error('Error playing audio:', error);
      setError(`Unable to play audio. (${Date.now()})`);
    }
  };

  const setStopRecording = async () => {
    // await new Promise(resolve => setTimeout(resolve, 2000));
    console.log('setStopRecording: Stopping streamed recording');
    setRecordingTime(0);
    stopStreamedRecording();
    stopRecording();
    setHasRecorded(true);
    setRemainingTries(prev => prev - 1);
    setIsCameraOn(false);
    setTimeout(() => {
      toggleCamera();
    }, 500);
  };

// Simplified submitStreamRecording
const submitStreamRecording = async() => {
  if(!isConnected){
    const connectionMessage = `You are offline. Please reconnect to continue.(${Date.now()})`;
    setToast({
      hasTitle: true,
      title: 'Connection Lost',
      message: connectionMessage.replace(/\(\d+\)$/, ''),
      type: 'error',
      isMobile: isMobile,
      isOpen: true,
    });
    setShowToast(true);
    return;
  }
  // mute TTS audio
  // await new Promise(resolve => setTimeout(resolve, 2000));
  if(questionAudioRef.current){
    questionAudioRef.current.pause();
    questionAudioRef.current.src = '';
  }

  await handleSubmitStreamRecording();

  const questionId = `question_${currentQuestionIndex}`;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  
  // Navigate or finalize
  if (!isLastQuestion) {
    setCurrentQuestionIndex(currentQuestionIndex + 1);
    setHasRecorded(false);
    setRemainingTries(3);
    goToNextQuestion();
  }else{
    navigate('/interview/success');  }
};

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        padding: isMobile ? '1rem' : '2rem',
        paddingTop: '0',
      }}
    >
      {toast && (
        <ToastMessage
          {...toast}
          isOpen={showToast}
          onClose={() => setShowToast(false)}
        />
      )}
      <div
        style={{
          width: isMobile ? '100%' : '70%',
          textAlign: 'left',
          padding: '0',
          borderRadius: '8px',
          marginTop: '0',
        }}
      >
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Spinner />
          </div>
        ) : job ? (
          <JobCard job={job} isMobile={isMobile} />
        ) : (
          <div>No job information available</div>
        )}

        {/* Main content area - two columns on desktop, stacked on mobile */}
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Spinner />
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '2rem',
              minHeight: 'calc(100vh - 400px)', 
            }}
        >
          {/* Left Column - Question */}
          <div
            style={{
              flex: isMobile ? '1' : '1',
            }}
          >
            {/* Question Counter */}
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ color: '#555', fontWeight: '500' }}>
                Question {currentQuestionIndex + 1} of {totalQuestions}
              </p>
            </div>

            {/* Question Text with Back Button */}
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                marginBottom: isMobile ? '1.2rem' : '1.5rem',
              }}
            >
              <h2
                style={{
                  fontSize: '1.25rem',
                  fontWeight: '500',
                  color: '#333',
                  margin: '0',
                }}
              >
                "{currentQuestion.questionText}"
              </h2>
            </div>
          </div>

          {/* Right Column - Camera and Controls */}
          <div style={{ flex: isMobile ? '1' : '1' }}>
            {/* Camera Preview with Gradient Overlay */}
            <div
              style={{
                width: '100%',
                aspectRatio: '4/3',
                position: 'relative',
                borderRadius: '8px',
                border: '1px solid #e0e4e8',
                marginBottom: '1rem',
                overflow: 'hidden',
              }}
            >
              {/* Always render video element but control visibility */}
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: isCameraOn ? 'block' : 'none',
                }}
              />

              {/* Hidden video for playback */}
              <video
                ref={playbackVideoRef}
                playsInline
                controls={false}
                muted={false}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'none',
                }}
              />

              {/* Placeholder with gradient when camera is off */}
              {!isCameraOn && (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    backgroundImage:
                      hasRecorded && recordedUrl
                        ? 'none'
                        : `url(${onboardingData.assets.cameraPlaceholder})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    position: 'relative',
                  }}
                >
                  {/* Show video preview if we have a recording */}
                  {hasRecorded && recordedUrl && (
                    <video
                      ref={playbackVideoRef}
                      playsInline
                      controls={false}
                      muted={true}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                      src={recordedUrl}
                    />
                  )}

                  {/* Gradient overlay at the bottom */}
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '100%',
                      height: '70%',
                      background: hasRecorded
                        ? 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0) 100%)'
                        : 'none',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'space-between',
                      padding: '0 20px 20px 20px',
                    }}
                  >
                    {/* Only show buttons after recording has occurred */}
                    {hasRecorded && !isRecording && (
                      <>
                        {/* Play button - left corner */}
                        <button
                          onClick={playRecording}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'white',
                            fontSize: '16px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <img
                            src="/svgs/play-button.svg"
                            alt="Play"
                            style={{ width: '16px', height: '16px' }}
                          />
                        </button>

                        {/* Audio button - right corner */}
                        <button
                          onClick={playAudio}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'white',
                            fontSize: '16px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <img
                            src="/svgs/audio-replay.svg"
                            alt="Audio"
                            style={{ width: '16px', height: '16px' }}
                          />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Recording Controls */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1rem',
              }}
            >
              {/* Show different controls based on recording state */}
              {!isRecording && !hasRecorded ? (
                <>
                  <button
                    onClick={() => handleButtonClick('start', async () => {
                      await handleRecordButtonClick();
                    })}
                    disabled={processingButtons.start}
                    style={{
                      padding: isMobile ? '0.4rem 0.6rem' : '0.5rem 1.5rem',
                      backgroundColor: '#0d6efd',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                  >
                    {isStartButtonLoading ? <Spinner size="sm" color="light" /> : 'Start Recording'}
                  </button>

                  <div
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#e9ecef',
                      borderRadius: '16px',
                      fontSize: '0.85rem',
                      color: '#6c757d',
                    }}
                  >
                    {remainingTries} tries left
                  </div>
                </>
              ) : isRecording ? (
                <>
                  <button
                    onClick={() => handleButtonClick('stop', async () => {
                      await setStopRecording();
                    })}
                    disabled={processingButtons.stop}
                    style={{
                      padding: isMobile ? '0.4rem 0.6rem' : '0.5rem 1.5rem',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                  >
                    {processingButtons.stop ? <Spinner size="sm" color="light"/> : 'Stop Recording'}
                  </button>
                  {
                    recordingTime >= 90 && (
                      <span
                        style={{
                          padding: '0.2rem 0.8rem',
                          borderRadius: '10px',
                          fontWeight: '500',
                          fontSize: '0.85rem',
                          color: '#6c757d',
                        }}
                      >
                        {formatTimeRemaining()}
                      </span>
                    )
                  }

                  <div
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#e9ecef',
                      borderRadius: '16px',
                      fontSize: '0.85rem',
                      color: '#6c757d',
                    }}
                    id="remaining-tries-left"
                  >
                    {remainingTries} tries left
                  </div>
                </>
              )  
              : /* Post-recording UI */ 
              !isCameraOn ? 
              (isMobile ? (
                /* Mobile layout - stacked */
                <>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                      gap: '0.75rem',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <button
                        onClick={() => {
                          handleButtonClick('reRecord', async () => {
                            await handleRecordButtonClick();
                          });
                        }}
                        disabled={remainingTries === 0}
                        style={{
                          flex: '1',
                          marginRight: '0.5rem',
                          padding: '0.5rem 1rem',
                          backgroundColor: 'white',
                          color: '#333',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          fontWeight: '500',
                          cursor: remainingTries === 0 ? 'not-allowed' : 'pointer',
                          opacity: remainingTries === 0 ? 0.5 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          justifyContent: 'center',
                          height: '40px',
                        }}
                        id="rerecord-button"
                      >
                        {processingButtons.reRecord ? (
                          <Spinner size="sm" />
                        ) : (
                          <>
                        <img src="/svgs/retry.svg" alt="Rerecord" />
                        Rerecord
                          </>
                        )}
                      </button>

                      <button
                        disabled={processingButtons.submit}
                        onClick={() => handleButtonClick('submit', async () => submitStreamRecording())}
                        style={{
                          flex: '1',
                          marginLeft: '0.5rem',
                          padding: '0.5rem 1rem',
                          backgroundColor: '#11807a',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          gap: '0.5rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '40px',
                        }}
                        id="submit-interview-answer-button"
                      >
                        {processingButtons.submit ? <Spinner size="sm" color="light" /> : <><img src="/svgs/paperplane.svg" alt="Submit" /> Submit</>}
                      </button>
                    </div>

                    <div
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#e9ecef',
                        borderRadius: '16px',
                        fontSize: '0.85rem',
                        color: '#6c757d',
                        textAlign: 'center',
                        width: 'fit-content',
                        margin: '0 auto',
                      }}
                    >
                      {remainingTries} tries left
                    </div>
                  </div>
                </>
              ) : (
                /* Desktop layout - side by side with tries left in middle */
                <>
                  <div
                    style={{ display: 'flex', gap: '1rem', alignItems: 'center', width: '100%' }}
                  >
                    <button
                      onClick={() => {
                        handleButtonClick('reRecord', async () => {
                          await handleRecordButtonClick();
                        });
                      }}
                      disabled={processingButtons.reRecord}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: 'white',
                        color: '#333',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        fontWeight: '500',
                        cursor: remainingTries === 0 ? 'not-allowed' : 'pointer',
                        opacity: remainingTries === 0 ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        justifyContent: 'center',
                        width: '140px',
                      }}
                    >
                      {processingButtons.reRecord ? <Spinner size="sm" /> : <><img src="/svgs/retry.svg" alt="Rerecord" />
                        Rerecord</>}
                    </button>

                    <div
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#e9ecef',
                        borderRadius: '16px',
                        fontSize: '0.85rem',
                        color: '#6c757d',
                        margin: '0 auto',
                      }}
                    >
                      {remainingTries} tries left
                    </div>

                    <button
                      onClick={() => handleButtonClick('submit', async () => submitStreamRecording())}
                      disabled={processingButtons.submit}
                      style={{
                        padding: '0.5rem 1.5rem',
                        backgroundColor: '#11807a',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        justifyContent: 'center',
                        width: '140px',
                        height: '40px',
                      }}
                    >
                      {processingButtons.submit ? <Spinner size="sm" color="light" /> : <><img src="/svgs/paperplane.svg" alt="Submit" /> Submit</>}
                    </button>
                  </div>
                </>
              )) : <></>}
            </div>

            {/* Support Message */}
            <div style={{
              textAlign: 'center',
              marginBottom: '1rem',
              marginTop: isMobile ? '1.5rem' : '1.8rem',
              fontSize: isMobile ? '0.8rem' : '0.9rem',
              color: '#6c757d',
            }}>
              Need Assistance? Please email us at <a href="mailto:jobseeker-support@jobtarget.com" style={{ color: '#6c757d', textDecoration: 'underline' }}>JobSeeker Support</a>.
            </div>
          </div>
        </div>)}
      </div>
    </div>
  );
};

export default Interview;
